import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import usePageTitle from '../hooks/usePageTitle';

function ContactPage() {
  usePageTitle('Contact');

  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState('');

  // Spam signals the endpoint checks. `honeypot` is a hidden field a person
  // never sees and so never fills; `openedAt` lets the endpoint tell a typed
  // message from an instant scripted post.
  const [honeypot, setHoneypot] = useState('');
  const openedAt = useRef(Date.now());

  // Length caps that match the ones the endpoint enforces.
  const MAX_SUBJECT = 200;
  const MAX_MESSAGE = 5000;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Every attempt starts from a clean slate. The form used to keep the
    // error of the previous attempt, so a second submit could show one
    // message while a different rule was the one that failed.
    setStatus('idle');
    setErrorMessage('');

    // Basic validation
    if (!email.trim() || !subject.trim() || !message.trim()) {
      setStatus('error');
      setErrorMessage('Please fill in all fields.');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setStatus('sending');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          subject,
          message,
          website: honeypot,
          elapsedMs: Date.now() - openedAt.current,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setEmail('');
        setSubject('');
        setMessage('');
        setHoneypot('');
        openedAt.current = Date.now();
      } else {
        setStatus('error');
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 py-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <h1 
            className="text-4xl md:text-5xl font-bold text-center mb-4"
            style={{ color: '#a60533' }}
          >
            Contact Support
          </h1>
          
          {/* Subtitle */}
          <p className="text-gray-600 text-center mb-10 text-lg">
            A member of our team will receive your message and respond as soon as possible.
          </p>

          {/* Success Message */}
          {status === 'success' && (
            <div role="status" className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-green-700 font-medium">
                  Your message has been sent successfully! We'll get back to you soon.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {status === 'error' && errorMessage && (
            <div role="alert" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 font-medium">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Contact Form */}
          {/* noValidate: the form owns its validation. The browser's own
              email check runs BEFORE the submit handler and blocks it, which
              left the tooltip and a stale banner on screen at once. */}
          {/* The name makes the form a landmark that assistive technology
              can announce and jump to. */}
          <form onSubmit={handleSubmit} noValidate aria-label="Contact form" className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
            {/* Email Field */}
            <div>
              {/* Honeypot: hidden from people and from assistive technology,
                  and out of the tab order. Only an automated filler completes
                  it. It is nested inside this field rather than placed beside
                  it, because the form's `space-y-6` rule puts a margin on
                  every direct child after the first. */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  padding: 0,
                  margin: '-1px',
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
              />
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Your Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                maxLength={254}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all outline-none"
                style={{ '--tw-ring-color': '#a60533' }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#a60533';
                  e.target.style.boxShadow = '0 0 0 3px rgba(166, 5, 51, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={status === 'sending'}
              />
            </div>

            {/* Subject Field */}
            <div>
              <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                value={subject}
                maxLength={MAX_SUBJECT}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What is your message about?"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all outline-none"
                onFocus={(e) => {
                  e.target.style.borderColor = '#a60533';
                  e.target.style.boxShadow = '0 0 0 3px rgba(166, 5, 51, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={status === 'sending'}
              />
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Message
              </label>
              <textarea
                id="message"
                value={message}
                maxLength={MAX_MESSAGE}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us how we can help you..."
                rows={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all outline-none resize-none"
                onFocus={(e) => {
                  e.target.style.borderColor = '#a60533';
                  e.target.style.boxShadow = '0 0 0 3px rgba(166, 5, 51, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={status === 'sending'}
              />
            </div>

            {/* Send Button */}
            <button
              type="submit"
              disabled={status === 'sending'}
              className="w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-200 flex items-center justify-center space-x-2"
              style={{ 
                backgroundColor: status === 'sending' ? '#d1d5db' : '#a60533',
                cursor: status === 'sending' ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={(e) => {
                if (status !== 'sending') {
                  e.target.style.backgroundColor = '#8a042b';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (status !== 'sending') {
                  e.target.style.backgroundColor = '#a60533';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              {status === 'sending' ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-gray-500">Sending...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Send Message</span>
                </>
              )}
            </button>
          </form>

          {/* Back to Home Link */}
          <div className="text-center mt-8">
            <Link 
              to="/" 
              className="text-gray-600 hover:text-gray-800 transition-colors inline-flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ContactPage;

